import { Goal, GoalContribution } from '../../services/goalApi';
import { parseLocalDate } from '../../utils/bills';
import { GoalOutlook } from './goalOutlook';

/** The bars show at most this many months up to the current one. */
export const PAST_MONTHS = 6;
/** ...and at most this many planned months after it, for goals with a deadline. */
export const PLANNED_MONTHS = 6;

export interface MonthColumn {
  /** YYYY-MM */
  key: string;
  /** the first day of the month */
  date: Date;
  /** deposits made in the month, or the deposit the deadline asks for */
  amount: number;
  kind: 'past' | 'current' | 'planned';
}

export interface BalancePoint {
  date: Date;
  value: number;
}

const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1);
const monthsBetween = (from: Date, to: Date) =>
  (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
export const monthKeyOf = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const createdAt = (goal: Goal): Date | null => {
  if (!goal.createdAt) return null;
  const date = new Date(goal.createdAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** When the goal's story starts: its creation or its oldest entry, whichever came first. */
const storyStart = (goal: Goal, trail: GoalContribution[], now: Date): Date => {
  const dates = trail.map((entry) => parseLocalDate(entry.date)).filter((date) => !Number.isNaN(date.getTime()));
  const created = createdAt(goal);
  if (created) dates.push(created);
  const first = dates.reduce<Date>((min, date) => (date < min ? date : min), now);
  return first > now ? now : first;
};

/**
 * The columns of the "deposits per month" bars: the months the goal has
 * existed, up to the current one, with what was deposited in each, then, for
 * a goal with a deadline, the months left with the deposit each one needs.
 * Adjustments never count: they move the balance, not the saving habit —
 * the same rule the pace follows.
 */
export const goalMonths = (
  goal: Goal,
  trail: GoalContribution[],
  outlook: GoalOutlook,
  now: Date = new Date(),
): MonthColumn[] => {
  const current = monthStart(now);
  const span = Math.min(Math.max(monthsBetween(monthStart(storyStart(goal, trail, now)), current) + 1, 1), PAST_MONTHS);

  const totals = new Map<string, number>();
  trail
    .filter((entry) => (entry.kind ?? 'deposit') === 'deposit')
    .forEach((entry) => {
      const key = entry.date.slice(0, 7);
      totals.set(key, (totals.get(key) ?? 0) + (Number(entry.amount) || 0));
    });

  const past: MonthColumn[] = Array.from({ length: span }, (_, index) => {
    const date = addMonths(current, index - (span - 1));
    const key = monthKeyOf(date);
    return { key, date, amount: totals.get(key) ?? 0, kind: index === span - 1 ? 'current' : 'past' };
  });

  const planned: MonthColumn[] =
    outlook?.kind === 'path' && outlook.needed > 0
      ? Array.from({ length: Math.min(outlook.monthsToDeadline, PLANNED_MONTHS) }, (_, index) => {
          const date = addMonths(current, index + 1);
          return { key: monthKeyOf(date), date, amount: outlook.needed, kind: 'planned' as const };
        })
      : [];

  return [...past, ...planned];
};

/**
 * Trims the columns to what fits: the planned months closest to now and the
 * most recent past ones, the current month always among them.
 */
export const fitColumns = (columns: MonthColumn[], budget: number): MonthColumn[] => {
  const room = Math.max(Math.floor(budget), 2);
  if (columns.length <= room) return columns;
  const past = columns.filter((column) => column.kind !== 'planned');
  const planned = columns.filter((column) => column.kind === 'planned');
  let plannedKept = Math.min(planned.length, Math.floor(room / 2));
  const pastKept = Math.min(past.length, room - plannedKept);
  plannedKept = Math.min(planned.length, room - pastKept);
  return [...past.slice(past.length - pastKept), ...planned.slice(0, plannedKept)];
};

/**
 * The balance over time, for the line before "today": one point per entry,
 * adjustments included, ending at the stored balance. Whatever the trail
 * does not explain sits at the start, so the line always lands on the
 * number the goal shows.
 */
export const balanceHistory = (goal: Goal, trail: GoalContribution[], now: Date = new Date()): BalancePoint[] => {
  const entries = trail
    .map((entry) => ({ date: parseLocalDate(entry.date), amount: Number(entry.amount) || 0 }))
    .filter((entry) => !Number.isNaN(entry.date.getTime()) && entry.date <= now)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  const current = Math.max(0, Number(goal.currentAmount) || 0);
  let running = current - entries.reduce((sum, entry) => sum + entry.amount, 0);

  const points: BalancePoint[] = [{ date: storyStart(goal, trail, now), value: Math.max(running, 0) }];
  entries.forEach((entry) => {
    running += entry.amount;
    points.push({ date: entry.date, value: Math.max(running, 0) });
  });
  points.push({ date: now, value: current });
  return points;
};
